import { mergeComponentClassNames, mergeComponentStyleValues } from './internal';
import { materializeRuntimeNode } from '../render/runtime-component';
import { normalizeChildrenToNodes } from '../internal/children';
import { styleValueToString } from '../internal/style';
const UNSUPPORTED_AS_CHILD_ROOT_TYPES = new Set([
    'TextNode',
    'Show',
    'For',
    'Match',
    'SwitchFlow',
]);
const SEMANTIC_TAGGABLE_CHILD_TYPES = new Set([
    'Box',
    'Flex',
    'Grid',
    'GridItem',
    'Stack',
    'StackRow',
    'StackCol',
    'Text',
]);
const COMPOSABLE_EVENT_PROP_NAMES = [
    'onClick',
    'onContextMenu',
    'onMouseEnter',
    'onMouseLeave',
    'onFocus',
    'onBlur',
    'onKeyDown',
    'onInput',
    'onChange',
];
export function composeAsChildNode(options) {
    const concreteChild = resolveAsChildRoot(options.componentType, options.children);
    const child = options.transformChild?.(concreteChild) ?? concreteChild;
    if (UNSUPPORTED_AS_CHILD_ROOT_TYPES.has(child.type)) {
        throw new Error(`[smoothie/ui] ${options.componentType} with asChild requires a concrete child root, not ${child.type}.`);
    }
    if (options.semanticTagOverride && !SEMANTIC_TAGGABLE_CHILD_TYPES.has(child.type)) {
        if (child.type !== options.semanticTagOverride) {
            throw new Error(`[smoothie/ui] ${options.componentType} with asChild cannot change the semantic tag of ${child.type}.`);
        }
    }
    const semanticTagOverride = child.type === options.semanticTagOverride
        ? undefined
        : options.semanticTagOverride;
    const mergedProps = mergeProps(child.props, options.props, options.explicitDataUi, semanticTagOverride, options.eventOrder);
    const composedSchemaTypes = mergeComposedSchemaTypes(child, options.componentType);
    return {
        ...child,
        props: mergedProps,
        ...(composedSchemaTypes.length > 0 && { _runtimeComposedSchemaTypes: composedSchemaTypes }),
    };
}
export function resolveAsChildRoot(componentType, children) {
    const childNodes = normalizeChildrenToNodes(children);
    if (childNodes.length !== 1) {
        throw new Error(`[smoothie/ui] ${componentType} with asChild requires exactly one concrete child node.`);
    }
    const child = resolveConcreteChildRoot(childNodes[0], componentType);
    if (UNSUPPORTED_AS_CHILD_ROOT_TYPES.has(child.type)) {
        throw new Error(`[smoothie/ui] ${componentType} with asChild requires a concrete child root, not ${child.type}.`);
    }
    return child;
}
function resolveConcreteChildRoot(node, componentType) {
    let current = node;
    for (;;) {
        if (current._runtimeRender) {
            current = materializeRuntimeNode(current);
            continue;
        }
        if (current.type !== 'Fragment') {
            return current;
        }
        if (!canUnwrapFragment(current)) {
            throw new Error(`[smoothie/ui] ${componentType} with asChild requires a concrete child root, not ${current.type}.`);
        }
        current = current.children[0];
    }
}
function canUnwrapFragment(node) {
    if (node.text !== undefined) {
        return false;
    }
    return Object.keys(node.props).length === 0 && node.children.length === 1;
}
function mergeProps(childProps, outerProps, explicitDataUi, semanticTagOverride, eventOrder = 'child-first') {
    const merged = {
        ...outerProps,
        ...childProps,
    };
    merged.className = mergeComponentClassNames(outerProps.className, childProps.className);
    const outerStyle = styleValueToString(childlessStyleValue(outerProps.style));
    if (outerStyle || childProps.style !== undefined) {
        merged.style = mergeComponentStyleValues(outerStyle ?? '', childProps.style);
    }
    for (const eventPropName of COMPOSABLE_EVENT_PROP_NAMES) {
        const childHandler = childProps[eventPropName];
        const outerHandler = outerProps[eventPropName];
        if (typeof childHandler === 'function' && typeof outerHandler === 'function') {
            merged[eventPropName] = composeFunctions(childHandler, outerHandler, eventOrder);
            continue;
        }
        if (typeof childHandler === 'function') {
            merged[eventPropName] = childHandler;
            continue;
        }
        if (typeof outerHandler === 'function') {
            merged[eventPropName] = outerHandler;
        }
    }
    if (typeof childProps.ref === 'function' && typeof outerProps.ref === 'function') {
        merged.ref = composeRefs(childProps.ref, outerProps.ref);
    }
    else if (childProps.ref !== undefined) {
        merged.ref = childProps.ref;
    }
    else if (outerProps.ref !== undefined) {
        merged.ref = outerProps.ref;
    }
    if (explicitDataUi !== undefined) {
        merged['data-ui'] = explicitDataUi;
    }
    if (semanticTagOverride !== undefined) {
        merged['data-as'] = semanticTagOverride;
    }
    return merged;
}
function childlessStyleValue(value) {
    if (typeof value === 'string') {
        return value;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
    }
    return undefined;
}
function composeFunctions(childHandler, outerHandler, eventOrder) {
    return (...args) => {
        if (eventOrder === 'outer-first') {
            outerHandler(...args);
            if (!isDefaultPrevented(args[0])) {
                childHandler(...args);
            }
            return;
        }
        childHandler(...args);
        outerHandler(...args);
    };
}
function isDefaultPrevented(value) {
    return Boolean(value
        && typeof value === 'object'
        && 'defaultPrevented' in value
        && value.defaultPrevented);
}
function composeRefs(childRef, outerRef) {
    return (value) => {
        childRef(value);
        outerRef(value);
    };
}
function mergeComposedSchemaTypes(child, componentType) {
    const schemaTypes = new Set(child._runtimeComposedSchemaTypes ?? []);
    if (child.type !== componentType) {
        schemaTypes.add(componentType);
    }
    return [...schemaTypes].sort();
}
//# sourceMappingURL=as-child.js.map